import React, { useRef } from 'react';
import useTiltEffect from '../../hooks/useTiltEffect';

/**
 * GlassCard - Reusable glass-morphism card component
 * @param {string} variant - 'default' | 'streak' | 'discover' | 'resume' | 'interview' | 'quiz' | 'progress'
 * @param {boolean} hoverable - Enable hover lift effect
 * @param {boolean} tiltEffect - Enable 3D tilt on hover
 * @param {string} glowColor - Custom glow color on hover ('indigo' | 'cyan' | 'amber')
 * @param {function} onClick - Click handler
 * @param {object} style - Additional inline styles
 * @param {string} className - Additional CSS classes
 * @param {React.ReactNode} children - Card content
 */
const GlassCard = ({
    variant = 'default',
    hoverable = false,
    tiltEffect = false,
    glowColor,
    onClick,
    style = {},
    className = '',
    children,
}) => {
    const cardRef = useRef(null);
    const [isHovered, setIsHovered] = React.useState(false);

    // Apply tilt effect if enabled
    useTiltEffect(tiltEffect ? cardRef : { current: null });

    // Variant-specific gradient overlays
    const variantGlows = {
        streak: 'radial-gradient(circle at 30% 50%, rgba(245, 158, 11, 0.15), transparent 50%)',
        discover: 'radial-gradient(circle at 30% 30%, rgba(99, 102, 241, 0.15), transparent 50%)',
        resume: 'radial-gradient(circle at 70% 30%, rgba(34, 211, 238, 0.15), transparent 50%)',
        interview: 'radial-gradient(circle at 30% 70%, rgba(245, 158, 11, 0.15), transparent 50%)',
        quiz: 'radial-gradient(circle at 70% 70%, rgba(34, 211, 238, 0.15), transparent 50%)',
        progress: 'radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.15), transparent 50%)',
    };

    // Glow shadow colors
    const glowShadows = {
        indigo: '0 0 40px rgba(99, 102, 241, 0.3)',
        cyan: '0 0 40px rgba(34, 211, 238, 0.3)',
        amber: '0 0 40px rgba(245, 158, 11, 0.3)',
    };

    // Top border colors for action cards
    const topBorderColors = {
        discover: 'linear-gradient(90deg, var(--indigo-500), var(--cyan-400))',
        resume: 'linear-gradient(90deg, var(--cyan-400), var(--indigo-500))',
        interview: 'linear-gradient(90deg, var(--amber-500), var(--indigo-500))',
        quiz: 'linear-gradient(90deg, var(--cyan-400), var(--amber-500))',
        progress: 'linear-gradient(90deg, var(--indigo-500), var(--cyan-400))',
    };

    const baseStyles = {
        position: 'relative',
        background: 'var(--bg-card)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: hoverable && isHovered ? 'var(--border-medium)' : 'var(--border-subtle)',
        borderRadius: 'var(--radius-xl)',
        overflow: 'hidden',
        transition: 'all var(--duration-base) var(--ease-out)',
        cursor: onClick ? 'pointer' : 'default',
        transform: hoverable && isHovered && !tiltEffect ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hoverable && isHovered ? (glowColor ? glowShadows[glowColor] : 'var(--shadow-lg)') : 'none',
    };

    // Removed separate hoverStyles - all hover logic now in baseStyles for proper state reset

    // Streak variant has special background
    const streakStyles = variant === 'streak' ? {
        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(239, 68, 68, 0.05))',
        borderColor: 'rgba(245, 158, 11, 0.2)',
    } : {};

    return (
        <div
            ref={cardRef}
            className={className}
            style={{
                ...baseStyles,
                ...streakStyles,
                ...style,
            }}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Top border gradient for action cards */}
            {topBorderColors[variant] && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: topBorderColors[variant],
                        opacity: isHovered ? 1 : 0,
                        transition: 'opacity var(--duration-base)',
                    }}
                />
            )}

            {/* Glow overlay on hover */}
            {variantGlows[variant] && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: variantGlows[variant],
                        opacity: isHovered ? 1 : 0,
                        transition: 'opacity var(--duration-base)',
                        pointerEvents: 'none',
                    }}
                />
            )}

            {children}
        </div>
    );
};

export default GlassCard;
